// src/services/supportService.js
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import secureFetch from '../utils/secureFetch';

/**
 * Upload image to ImgBB with secure fetch for certificate pinning
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<string|null>} The URL of the uploaded image, or null if upload failed
 */
const uploadImageToImgBB = async (imageFile) => {
  // Get the API key from environment variables
  const IMGBB_API_KEY = process.env.REACT_APP_IMGBB_API_KEY;
  
  if (!IMGBB_API_KEY) {
    console.error('ImgBB API key not found in environment variables');
    return null;
  }
  
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('key', IMGBB_API_KEY);
    
    // Use secureFetch instead of regular fetch
    const response = await secureFetch('https://api.imgbb.com/1/upload', {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.success) {
      return data.data.url; // Return the direct image URL
    } else {
      console.error('ImgBB upload failed:', data);
      return null;
    }
  } catch (error) {
    console.error('Error uploading image to ImgBB:', error);
    return null;
  }
};

/**
 * Submit a support request with optional image
 */
export const submitSupportRequest = async (supportData) => {
  try {
    let imageUrl = null;
    
    // Upload image to ImgBB if provided
    if (supportData.image) {
      imageUrl = await uploadImageToImgBB(supportData.image);
    }
    
    // Add the support request to Firestore
    const docRef = await addDoc(collection(db, "support_requests"), {
      email: supportData.email,
      message: supportData.message,
      category: supportData.category || 'General',
      imageUrl: imageUrl,
      status: 'new', 
      createdAt: serverTimestamp(), 
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      }
    });
    
    return {
      success: true,
      id: docRef.id,
      message: "Support request submitted successfully"
    };
  } catch (error) {
    console.error("Error submitting support request:", error);
    return {
      success: false,
      error: error.message || "Failed to submit support request"
    };
  }
};